import React, {
  FC,
  HTMLInputTypeAttribute,
  KeyboardEvent,
  useState,
} from "react";

import styles from "./TextInput.module.css";
import { Typography } from "../Typography";
import classNames from "classnames";

export interface TextInputProps {
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search"
    | undefined;
  disabled?: boolean;
  value: string;
  placeHolder?: string;
  onChangeText: (text: string) => void;
  returnKeyLabel?: string;
  onFocus?: (e?: any) => void;
  onBlur?: (e?: any) => void;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  onKeyUp?: (e: KeyboardEvent<HTMLInputElement>) => void;
  maxLength?: number;
  nativeRef?: React.Ref<any>;
  label?: string;
  error?: string | false;
  type?: HTMLInputTypeAttribute;
  style?: any;
  name?: string;
  autoComplete?: string;
  className?: string;
  ariaLabel?: string;
  ariaDescribedBy?: string;
  ariaInvalid?: boolean;
  required?: boolean;
}

const TextInput: FC<TextInputProps> = ({
  inputMode,
  disabled = false,
  value,
  onChangeText,
  placeHolder,
  maxLength,
  onFocus,
  onBlur,
  nativeRef,
  label,
  error,
  type,
  style,
  name,
  autoComplete,
  className,
  onKeyDown,
  onKeyUp,
  ariaLabel = "",
  ariaDescribedBy,
  ariaInvalid,
  required,
}) => {
  const [randomName] = useState(Math.random() + "");

  return (
    <>
      {label && (
        <label htmlFor={randomName}>
          <Typography variant="body3">{label}</Typography>
        </label>
      )}
      <input
        data-testid="text-input"
        inputMode={inputMode}
        type={type}
        required={required}
        name={name || randomName}
        className={classNames(
          styles.input,
          error && styles.inputError,
          className
        )}
        value={value}
        onChange={(e) => {
          const typesToTrim: HTMLInputTypeAttribute[] = [
            "email",
            "date",
            "number",
          ];
          if (typesToTrim.includes(type ?? "text")) {
            onChangeText(e.target.value?.trim());
          } else {
            onChangeText(e.target.value);
          }
        }}
        placeholder={placeHolder}
        maxLength={maxLength}
        ref={nativeRef}
        onFocus={onFocus}
        onBlur={onBlur}
        disabled={disabled}
        style={style}
        autoComplete={autoComplete}
        onKeyDown={onKeyDown}
        onKeyUp={onKeyUp}
        aria-label={ariaLabel}
        aria-invalid={ariaInvalid ? "true" : "false"}
        aria-describedby={ariaDescribedBy}
      />
      {error && (
        <div>
          <Typography variant="body3" color={"error"}>
            {error}
          </Typography>
        </div>
      )}
    </>
  );
};

interface StackedTextInputProps extends TextInputProps {
  first?: boolean;
  last?: boolean;
}

export const StackedTextInput = ({
  first,
  last,
  ...rest
}: StackedTextInputProps) => {
  const style = {
    borderRadius: 0,
    borderTopWidth: first ? 1 : 0,
    borderTopLeftRadius: first ? 24 : 0,
    borderTopRightRadius: first ? 24 : 0,
    borderBottomLeftRadius: last ? 24 : 0,
    paddingLeft: 20,
  };
  return <TextInput {...rest} style={style} />;
};

export default TextInput;
